import type { Widget } from '../types/widget.types';

/**
 * Widget Registry
 * 
 * Central registry for managing dashboard widgets. Handles registration,
 * discovery, and filtering of widgets based on permissions.
 */
class WidgetRegistry {
    private widgets: Map<string, Widget> = new Map();

    /**
     * Register a widget
     */
    register(widget: Widget): void {
        if (this.widgets.has(widget.id)) {
            console.warn(`Widget with id "${widget.id}" is already registered. Overwriting.`);
        }
        this.widgets.set(widget.id, widget);
    }

    /**
     * Unregister a widget
     */
    unregister(widgetId: string): void {
        this.widgets.delete(widgetId);
    }

    /**
     * Get a specific widget by ID
     */
    get(widgetId: string): Widget | undefined {
        return this.widgets.get(widgetId);
    }

    /**
     * Get all registered widgets
     */
    getAll(): Widget[] {
        return Array.from(this.widgets.values());
    }

    /**
     * Get widgets by category
     */
    getByCategory(category: string): Widget[] {
        return this.getAll().filter(w => w.category === category);
    }

    /**
     * Get widgets available to user based on permissions
     * @param permissions Array of menu permission keys the user has access to
     */
    getAvailable(permissions: string[]): Widget[] {
        return this.getAll().filter(widget => {
            // If no permission required, widget is available to everyone
            if (!widget.requiredPermission) {
                return true;
            }
            // Check if user has the required permission
            return permissions.includes(widget.requiredPermission);
        });
    }

    /**
     * Get default enabled widgets for a new user
     */
    getDefaults(): Widget[] {
        return this.getAll()
            .filter(w => w.defaultEnabled)
            .sort((a, b) => a.order - b.order);
    }

    /**
     * Clear all registered widgets (mainly for testing)
     */
    clear(): void {
        this.widgets.clear();
    }
}

// Export singleton instance
export const widgetRegistry = new WidgetRegistry();
